export class ChartHelper {
	/**
	 * By calculating the max value of values,
	 * this will return minimum value which is larger than max value
	 * and can be divided nicely on steps
	 */
	static getMaxStepValue(maxValue: number, steps: number) {
		// "Nice numbers" axis: round the per-step size up to 1/2/2.5/5 × a power of ten, then return
		// step × steps as the axis max. Works at ANY magnitude — fractional (CV ~0.9 → axis 0–1) and
		// large (746,878 → 0–1,000,000) alike. The previous version ceil()'d to an integer and floored
		// the step at 10, so any data ≤ ~1 collapsed to a 0–50 axis with invisible bars.
		if (!isFinite(maxValue) || maxValue <= 0 || steps <= 0) { return steps > 0 ? steps : 1; }

		const rawStep = maxValue / steps;
		const mag     = Math.pow(10, Math.floor(Math.log10(rawStep)));
		const norm    = rawStep / mag;   // 1 ≤ norm < 10
		const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
		const step    = niceNorm * mag;

		// Guard against binary-float drift (e.g. 0.2 × 5 = 1.0000000000000002).
		return parseFloat((step * steps).toPrecision(12));
	}

	/**
	 * App locale used for number formatting across all charts. Set once from Angular's LOCALE_ID
	 * by the chart host (see CsChartComponent). Undefined falls back to the JS runtime locale.
	 */
	static locale: string | undefined = undefined;

	/**
	 * Compact, locale-aware number for axis ticks and in-chart labels (e.g. 78M, 1.2K).
	 * Keeps labels short where space is tight; use getValueLocale for exact tooltip values.
	 */
	static getValueFormatted(val, locale?: string) {
		const num = +val;
		if (!Number.isFinite(num)) { return String(val); }
		return new Intl.NumberFormat(locale ?? ChartHelper.locale,
			{ notation: 'compact', maximumFractionDigits: 1 }).format(num);
	}

	/**
	 * Full number with locale grouping for exact / tooltip values (e.g. 78,432,131 or 78.432.131).
	 */
	static getValueLocale(val, locale?: string) {
		const num = +val;
		if (!Number.isFinite(num)) { return String(val); }
		return new Intl.NumberFormat(locale ?? ChartHelper.locale,
			{ maximumFractionDigits: 2 }).format(num);
	}
}
